import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import "../../styles/Home-page-component/cta.css";

/* ── Animated typewriter words ── */
const WORDS = ["Automate.", "Scale.", "Dominate.", "Innovate.", "Win."];

function Typewriter() {
  const [wordIdx, setWordIdx] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [pause, setPause] = useState(false);

  useEffect(() => {
    if (pause) {
      const t = setTimeout(() => setPause(false), 1200);
      return () => clearTimeout(t);
    }
    const word = WORDS[wordIdx];
    if (!deleting) {
      if (displayed.length < word.length) {
        const t = setTimeout(() => setDisplayed(word.slice(0, displayed.length + 1)), 90);
        return () => clearTimeout(t);
      } else {
        setPause(true);
        setDeleting(true);
      }
    } else {
      if (displayed.length > 0) {
        const t = setTimeout(() => setDisplayed(word.slice(0, displayed.length - 1)), 55);
        return () => clearTimeout(t);
      } else {
        setDeleting(false);
        setWordIdx((i) => (i + 1) % WORDS.length);
      }
    }
  }, [displayed, deleting, pause, wordIdx]);

  return (
    <span className="typewriter">
      {displayed}
      <span className="cursor">|</span>
    </span>
  );
}

/* ── Floating particle canvas ── */
function ParticleCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let W, H, particles, animId;

    const resize = () => {
      W = canvas.width  = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    };

    const makeParticle = () => ({
      x: Math.random() * (W || 1200),
      y: Math.random() * (H || 500),
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.6 + 0.4,
      a: Math.random() * 0.55 + 0.1,
    });

    const init = () => {
      resize();
      particles = Array.from({ length: 90 }, makeParticle);
    };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(47,128,237,${p.a})`;
        ctx.fill();
      });

      // draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 110) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(47,128,237,${0.12 * (1 - dist / 110)})`;
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };

    init();
    draw();
    window.addEventListener("resize", () => { init(); });
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="cta-canvas" />;
}

/* ── Magnetic CTA button ── */
function MagneticButton({ href, children, variant = "primary" }) {
  const btnRef = useRef(null);

  const onMove = useCallback((e) => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) * 0.28;
    const dy = (e.clientY - cy) * 0.28;
    btn.style.transform = `translate(${dx}px, ${dy}px) scale(1.04)`;
  }, []);

  const onLeave = useCallback(() => {
    const btn = btnRef.current;
    if (btn) btn.style.transform = "translate(0,0) scale(1)";
  }, []);

  return (
    <Link
      to={href}
      ref={btnRef}
      className={`cta-btn ${variant}`}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      <span className="btn-inner">{children}</span>
      {variant === "primary" && <span className="btn-shine" />}
    </Link>
  );
}

/* ── Live trust stats ticker ── */
const STATS = [
  { value: "500+",  label: "Enterprises Served" },
  { value: "99.9%", label: "Uptime SLA" },
  { value: "10×",   label: "Avg. Productivity Gain" },
  { value: "<48h",  label: "Onboarding Time" },
  { value: "4.9★",  label: "Client Rating" },
];

function StatsTicker() {
  return (
    <div className="ticker-wrap" aria-hidden>
      <div className="ticker-track">
        {[...STATS, ...STATS].map((s, i) => (
          <div className="ticker-item" key={i}>
            <span className="t-val">{s.value}</span>
            <span className="t-lbl">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════ CTA SECTION ══════════════════ */
const CTASection = () => {
  return (
    <section className="cta-section">
      <ParticleCanvas />

      {/* grid overlay */}
      <div className="cta-grid-bg" aria-hidden />

      {/* glows */}
      <div className="cta-glow cta-glow-l" aria-hidden />
      <div className="cta-glow cta-glow-r" aria-hidden />
      <div className="cta-glow cta-glow-c" aria-hidden />

      <div className="cta-container">

        {/* eyebrow */}
        <div className="cta-eyebrow">
          <span className="eyebrow-dot" />
          Enterprise AI Platform · Available Now
        </div>

        {/* headline */}
        <h2 className="cta-headline">
          Ready to&nbsp;
          <Typewriter />
        </h2>

        <p className="cta-subheadline">
          Join 500+ forward-thinking companies using Incogniflex to deploy AI agents,
          automate engineering workflows, and outpace competition — all without hiring an army.
        </p>

        {/* feature chips */}
        <ul className="feature-chips" aria-label="Key features">
          {[
            "🤖 AI Agents deployed in days",
            "☁️ Cloud-native on AWS / GCP / Azure",
            "🔒 SOC 2 · GDPR · HIPAA compliant",
            "⚡ 10× faster than in-house builds",
          ].map((f) => (
            <li key={f} className="chip">{f}</li>
          ))}
        </ul>

        {/* CTAs */}
        <div className="cta-actions">
          <MagneticButton href="/contact" variant="primary">
            🚀 Schedule Free Consultation
          </MagneticButton>
          {/* <MagneticButton href="/demo" variant="secondary">
            Watch 2-min Demo →
          </MagneticButton> */}
        </div>

        {/* social proof */}
        <p className="cta-proof">
          No credit card required · Setup in &lt;48 hours · Cancel anytime
        </p>

        {/* divider */}
        <div className="cta-divider" aria-hidden />

        {/* stats ticker */}
        <StatsTicker />

      </div>
    </section>
  );
};

export default CTASection;